import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Key, Shield, X, AlertTriangle, Check } from 'lucide-react';

export default function ChangePasswordModal({ isOpen, show, onClose, onSuccess, required = false, userId = null }) {
    const isVisible = isOpen || show; // Support both props
    const isAdminMode = !!userId;

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { changePassword } = useAuth();

    // Reset form when modal opens
    useEffect(() => {
        if (isVisible) {
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setError('');
        }
    }, [isVisible]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError('Nova senha deve ter pelo menos 6 caracteres');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        setLoading(true);

        try {
            if (isAdminMode) {
                // Admin resetting another user's password
                await api.post(`/users/${userId}/reset-password`, { newPassword });
            } else {
                // User changing their own password
                const result = await changePassword(oldPassword, newPassword);
                if (!result.success) {
                    throw new Error(result.error);
                }
            }

            if (onSuccess) onSuccess();
            if (!required && onClose) onClose();

            if (isAdminMode) {
                alert('Senha redefinida com sucesso! O usuário deverá trocá-la no próximo login.');
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Erro ao alterar senha');
        } finally {
            setLoading(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 dark:border-gray-700 animate-in fade-in zoom-in duration-200">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        {isAdminMode ? <Shield className="w-5 h-5 text-blue-600" /> : <Key className="w-5 h-5 text-blue-600" />}
                        {isAdminMode ? 'Redefinir Senha (Admin)' : (required ? 'Troca de Senha Obrigatória' : 'Trocar Senha')}
                    </h3>
                    {!required && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-4">
                        {required && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 text-sm p-3 rounded-lg border border-amber-100 dark:border-amber-900/50 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Você precisa trocar sua senha antes de continuar
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg border border-red-100 dark:border-red-900/50">
                                {error}
                            </div>
                        )}

                        {!isAdminMode && (
                            <div className="space-y-1.5">
                                <label htmlFor="oldPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Senha Atual
                                </label>
                                <input
                                    type="password"
                                    id="oldPassword"
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                />
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <label htmlFor="newPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Nova Senha
                            </label>
                            <input
                                type="password"
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={6}
                                disabled={loading}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400">Mínimo 6 caracteres</p>
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Confirmar Nova Senha
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                            />
                        </div>

                    </div>

                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3 rounded-b-xl">
                        {!required && (
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                            >
                                Cancelar
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    {isAdminMode ? <Check className="w-4 h-4" /> : <Key className="w-4 h-4" />}
                                    {isAdminMode ? 'Redefinir Senha' : 'Salvar Nova Senha'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
